import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { DropdownSettings } from 'angular2-multiselect-dropdown/lib/multiselect.interface';
import { ScwMatSelectRule } from './scw-mat-select.model';
import { ScwMatInputRule } from '../scw-mat-input/scw-mat-input.model';
import { ScwMatSearchBehavior, ScwMatSearchButtonPosition } from '../scw-mat-search/scw-mat-search.model';
import * as _ from 'lodash';
import { ScwMatSearchComponent } from '../scw-mat-search/scw-mat-search.component';

@Component({
  selector: 'scw-mat-select',
  templateUrl: './scw-mat-select.component.html',
  styleUrls: ['./scw-mat-select.component.scss'],
})
export class ScwMatSelectComponent implements OnInit, OnChanges, OnDestroy {
  @ViewChild('searchComponent') searchComponent: ScwMatSearchComponent;

  @Input() inputModel: any;
  @Input() isValid: boolean = false;
  @Input() data: any[] = [];
  @Input() label: string;
  @Input() isLoading: boolean = false;
  @Input() rules: ScwMatSelectRule[] = [];
  @Input() hasErrors: boolean = false;
  @Input() errorText: string;
  @Input() singleSelection: boolean = true;
  @Input() limitSelection: number = null;
  @Input() text: string;
  @Input() enableCheckAll: boolean = false;
  @Input() selectAllText: string = 'Select All';
  @Input() unSelectAllText: string = 'Unselect All';
  @Input() filterSelectAllText: string = "Select all filtered results";
  @Input() filterUnSelectAllText: string = 'Unselect all filtered results';
  @Input() enableSearchFilter: boolean = false;
  @Input() searchBy: any[] = [];
  @Input() maxHeight: number = 300;
  @Input() badgeShowLimit: number = 1;
  @Input() classes: string = '';
  @Input() disabled: boolean = false;
  @Input() searchPlaceholderText: string = 'Search...';
  @Input() showCheckbox: boolean = false;
  @Input() noDataLabel: string = 'No Data';
  @Input() searchAutofocus: boolean = true;
  @Input() lazyLoading: boolean = false;
  @Input() labelKey: string = 'name';
  @Input() primaryKey: string = 'id';
  @Input() position: string = 'bottom';
  @Input() autoPosition: boolean = true;
  @Input() enableFilterSelectAll: boolean = true;
  @Input() selectGroup: boolean = false;
  @Input() addNewItemOnFilter: boolean = false;
  @Input() addNewButtonText: string = 'Add New';
  @Input() escapeToClose: boolean = true;
  @Input() clearAll: boolean = true;
  @Input() isRequired: boolean = false;
  @Input() noPadding: boolean = false;
  @Input() groupBy: string;
  @Input() tagToBody: boolean = false;
  @Input() belongsToFilterCardComponent: boolean = false;
  @Input() hint: string = null;
  @Input() advancedSelect: boolean = false;
  @Input() dataPropertyKey?: string;
  // parameters of scw-mat-search-component
  @Input() searchButtonPosition: ScwMatSearchButtonPosition = 'right';
  @Input() searchBehavior: ScwMatSearchBehavior = 'live';
  @Input() searchDelay: number = 600;
  @Input() searchMinLength: number = 0;
  @Input() searchMaxLength: number = 1000;
  @Input() searchPreventSameSearchString: boolean = true;
  @Input() clearSearchOnOpen: boolean = false;
  @Input() cItemTemplate: TemplateRef<any>;
  @Input() size: 'sm' | 'md' = 'md';

  @Output() onClose: EventEmitter<any> = new EventEmitter<any>();
  @Output() onSelect: EventEmitter<any> = new EventEmitter<any>();
  @Output() onDeSelect: EventEmitter<any> = new EventEmitter<any>();
  @Output() onSelectAll: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Output() onDeSelectAll: EventEmitter<any[]> = new EventEmitter<any[]>();
  @Output() onGroupSelect: EventEmitter<any> = new EventEmitter<any>();
  @Output() onGroupDeSelect: EventEmitter<any> = new EventEmitter<any>();
  @Output() onScrollToEnd: EventEmitter<any> = new EventEmitter<any>();
  @Output() inputModelChange: EventEmitter<any> = new EventEmitter<any>();
  @Output() isValidChange: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() onSearch: EventEmitter<string> = new EventEmitter<string>();
  @Output() onAdd: EventEmitter<void> = new EventEmitter<void>();
  @Output() onOpen: EventEmitter<void> = new EventEmitter<void>();

  public _ = _;
  public settings: DropdownSettings;
  public searchResult: any[] = [];
  private isAnyError: boolean = false;
  public search: string = '';
  public rawSearchString: string = '';

  constructor() {}

  public onCloseEmitter($event: any): void {
    this.onClose.emit($event);
  }

  public onSelectEmitter($event: any): void {
    this.onSelect.emit($event);
  }

  public onDeSelectEmitter($event: any): void {
    this.onDeSelect.emit($event);
  }

  public onSelectAllEmitter($event: any[]): void {
    this.onSelectAll.emit($event);
  }

  public onDeSelectAllEmitter($event: any[]): void {
    this.onDeSelectAll.emit($event);
  }

  public onGroupSelectEmitter($event: any): void {
    this.onGroupSelect.emit($event);
  }

  public onGroupDeSelectEmitter($event: any): void {
    this.onGroupDeSelect.emit($event);
  }

  public onScrollToEndEmitter($event: any): void {
    this.onScrollToEnd.emit($event);
  }

  public onOpenEmitter($event: any): void {
    if (this.clearSearchOnOpen) {
      this.rawSearchString = '';
      this.searchComponent.lastValidSearch = '';
      this.searchComponent.emitSearchString();
    }

    this.onOpen.emit($event);
  }

  public onSearchStringRefresh(): void {
    if (
      !(
        this.rawSearchString === undefined ||
        this.rawSearchString === null ||
        this.rawSearchString.length > this.searchMaxLength ||
        this.rawSearchString.length < this.searchMinLength ||
        (this.searchPreventSameSearchString && this.searchMinLength === this.inputModel)
      )
    ) {
      this.onSearch.emit(this.rawSearchString);
    }
  }

  private applySettings(): void {
    this.settings = {
      singleSelection: this.singleSelection,
      limitSelection: this.limitSelection,
      text: this.text,
      enableCheckAll: this.enableCheckAll,
      selectAllText: this.selectAllText,
      unSelectAllText: this.unSelectAllText,
      filterSelectAllText: this.filterSelectAllText,
      filterUnSelectAllText: this.filterUnSelectAllText,
      enableSearchFilter: this.enableSearchFilter,
      searchBy: this.searchBy,
      maxHeight: this.maxHeight,
      badgeShowLimit: this.badgeShowLimit,
      classes: this.classes,
      disabled: this.disabled,
      searchPlaceholderText: this.searchPlaceholderText,
      showCheckbox: this.showCheckbox,
      noDataLabel: this.noDataLabel,
      searchAutofocus: this.searchAutofocus,
      lazyLoading: this.lazyLoading,
      labelKey: this.labelKey,
      primaryKey: this.primaryKey,
      position: this.position,
      autoPosition: this.autoPosition,
      enableFilterSelectAll: this.enableFilterSelectAll,
      selectGroup: this.selectGroup,
      addNewItemOnFilter: this.addNewItemOnFilter,
      addNewButtonText: this.addNewButtonText,
      escapeToClose: this.escapeToClose,
      clearAll: this.clearAll,
      groupBy: this.groupBy,
      tagToBody: this.tagToBody,
    };

    if (this.advancedSelect) {
      this.searchButtonPosition = 'right';
      this.enableSearchFilter = true;
    }
  }

  public reset(): void {
    this.search = '';
    this.rawSearchString = '';
    this.inputModel = null;
    this.clearErrorMessage();
  }

  private isValidEqualizer(isValid: boolean): void {
    this.isValid = isValid;
    this.isValidChange.emit(this.isValid);
  }

  private showErrorMessage(message: string): void {
    this.isValidEqualizer(false);
    this.isAnyError = true;
    this.hasErrors = true;
    this.errorText = message ? message : '';
  }

  public clearErrorMessage(): void {
    this.isValidEqualizer(true);
    this.isAnyError = false;
    this.hasErrors = false;
    this.errorText = null;
  }

  private requiredRule(rule: ScwMatInputRule): void {
    if (!this.inputModel || this.inputModel.length === 0) {
      this.showErrorMessage(rule.message ?? 'Required');
    }
  }

  private customRule(rule: ScwMatInputRule): void {
    if (this.inputModel && rule.custom && !rule.validator(this.inputModel)) {
      this.showErrorMessage(rule.message);
    }
  }

  public checkRules(): void {
    if (this.rules.length === 0) {
      this.isValidEqualizer(true);
      return;
    }

    this.isAnyError = false;

    for (const rule of this.rules) {
      if (this.isAnyError) {
        return;
      }

      if ('required' in rule) {
        this.requiredRule(rule);
      }

      if ('custom' in rule) {
        this.customRule(rule);
      }
    }

    if (this.isAnyError) {
      return;
    }

    this.clearErrorMessage();
  }

  public onSearchString(searchString: string): void {
    if (searchString === undefined) {
      return;
    }

    this.search = searchString.trim().toLowerCase();

    if (this.searchBehavior === 'live') {
      this.searchResult = this.data.filter((value: any) => {
        return (value[this.labelKey] ?? '').trim().toLowerCase().includes(this.search);
      });
    } else {
      this.searchResult = this.data;
      this.onSearch.emit(searchString);
    }
  }

  public onAddEmitter(): void {
    if (!this.data) {
      this.data = [
        {
          [this.settings.primaryKey]: null,
          [this.settings.labelKey]: '',
          disabled: true,
        },
      ];
    }

    this.inputModel = [
      {
        [this.settings.primaryKey]: null,
        [this.settings.labelKey]: this.rawSearchString,
      },
    ];

    this.onNgModelChange();
    this.onAdd.emit();
  }

  public onNgModelChange(): void {
    this.checkRules();
    this.inputModelChange.emit(this.inputModel);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.applySettings();
  }

  public ngOnInit(): void {
    this.applySettings();
  }

  public ngOnDestroy(): void {}
}
